/**
 * FilterBar — thin adapter over FilterPanel.
 * Do not add independent flex/wrap layouts here; all chrome goes through FilterPanel.
 */
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { DatePicker } from './date-picker'
import { Input } from './ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SELECT_ALL_VALUE,
  selectValueFromExternal,
  selectValueToExternal,
} from './ui/select'
import { FilterPanel, FilterPanelField } from './filter-panel'

export type { ClearFiltersButtonProps } from './clear-filters-button'
export { ClearFiltersButton, countDeviationsFromDefaults } from './clear-filters-button'

interface FilterFieldBase {
  label: string
  minWidth?: number
  clearable?: boolean
  showLabel?: boolean
}

interface FilterFieldStandard extends FilterFieldBase {
  key: string
  type: 'text' | 'select' | 'date' | 'custom'
  placeholder?: string
  mode?: 'single' | 'range'
  options?: { label: string; value: string }[]
  render?: () => React.ReactNode
}

interface FilterFieldDateRange extends FilterFieldBase {
  type: 'dateRange'
  fromKey: string
  toKey: string
  placeholder?: string
  includeAllTimePreset?: boolean
}

export type FilterField = FilterFieldStandard | FilterFieldDateRange

interface FilterBarProps {
  filters: Record<string, string>
  onFilterChange: (key: string, value: string) => void
  onClearFilters: () => void
  fields: FilterField[]
  searchPlaceholder?: string
  searchLabel?: string
  searchKey?: string
  showLabels?: boolean
  className?: string
  defaultFilters?: Record<string, string>
  activeFilterCount?: number
  hideSearch?: boolean
  /** Optional title when rendering as a full panel (default: ui.filters.title) */
  title?: string
  description?: string
  collapsible?: boolean
  defaultOpen?: boolean
  /** When true, skip Card chrome (parent already provides outer chrome) */
  bare?: boolean
  showDatePresets?: boolean
  onApply?: () => void
  applyLabel?: string
  applyDisabled?: boolean
  extraActions?: React.ReactNode
}

export function computeActiveFilterCount(
  filters: Record<string, string>,
  fields: FilterField[],
  searchKey: string,
  defaultFilters?: Record<string, string>,
): number {
  if (!defaultFilters) {
    return Object.values(filters).filter(Boolean).length
  }

  let count = 0
  const searchVal = (filters[searchKey] || '').trim()
  const defaultSearch = (defaultFilters[searchKey] ?? '').trim()
  if (searchVal && searchVal !== defaultSearch) count++

  for (const field of fields) {
    if (field.type === 'custom') continue
    if (field.type === 'dateRange') {
      const from = (filters[field.fromKey] || '').trim()
      const to = (filters[field.toKey] || '').trim()
      const defaultFrom = (defaultFilters[field.fromKey] ?? '').trim()
      const defaultTo = (defaultFilters[field.toKey] ?? '').trim()
      if (from !== defaultFrom || to !== defaultTo) count++
    } else {
      const value = (filters[field.key] || '').trim()
      const defaultValue = (defaultFilters[field.key] ?? '').trim()
      if (value && value !== defaultValue) count++
    }
  }
  return count
}

export function FilterBar({
  filters,
  onFilterChange,
  onClearFilters,
  fields,
  searchPlaceholder,
  searchLabel,
  searchKey = 'search',
  showLabels = true,
  className,
  defaultFilters,
  activeFilterCount: activeFilterCountOverride,
  hideSearch = false,
  title,
  description,
  collapsible = true,
  defaultOpen = false,
  bare = true,
  showDatePresets = true,
  onApply,
  applyLabel,
  applyDisabled,
  extraActions,
}: FilterBarProps) {
  const { t } = useTranslation()
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('ui.searchPlaceholder')
  const resolvedSearchLabel = searchLabel ?? t('ui.searchLabel')
  const activeFilterCount =
    activeFilterCountOverride ??
    computeActiveFilterCount(filters, fields, searchKey, defaultFilters)

  const dateRangeField = fields.find((f): f is FilterFieldDateRange => f.type === 'dateRange')
  const nonDateFields = fields.filter((f) => f.type !== 'dateRange')

  const customActionFields = nonDateFields.filter(
    (f) => f.type === 'custom' && (f.key === 'apply' || f.key === 'extra_actions' || f.key === 'save'),
  )
  const gridFields = nonDateFields.filter(
    (f) => !(f.type === 'custom' && (f.key === 'apply' || f.key === 'extra_actions' || f.key === 'save')),
  )

  const derivedOnApply =
    onApply ??
    (() => {
      const applyField = customActionFields.find((f) => f.key === 'apply' && f.type === 'custom')
      if (applyField && applyField.type === 'custom' && applyField.render) {
        // Apply is rendered via extraActions when it's a custom field with its own button
        return undefined
      }
      return undefined
    })()

  const actionNodes = useMemo(() => {
    const nodes: React.ReactNode[] = []
    if (extraActions) nodes.push(extraActions)
    for (const field of customActionFields) {
      if (field.type === 'custom' && field.render) {
        nodes.push(<React.Fragment key={field.key}>{field.render()}</React.Fragment>)
      }
    }
    return nodes.length ? <>{nodes}</> : null
  }, [customActionFields, extraActions])

  // If there's an apply custom field, we rely on extraActions render; onApply prop takes precedence
  const hasApplyCustom = customActionFields.some((f) => f.key === 'apply')

  return (
    <FilterPanel
      title={title}
      description={description}
      collapsible={collapsible}
      defaultOpen={defaultOpen}
      activeCount={activeFilterCount}
      className={className}
      bare={bare}
      dateRange={
        dateRangeField
          ? {
              from: filters[dateRangeField.fromKey] || '',
              to: filters[dateRangeField.toKey] || '',
              onChange: ({ from, to }) => {
                onFilterChange(dateRangeField.fromKey, from)
                onFilterChange(dateRangeField.toKey, to)
              },
              showPresets: showDatePresets,
              includeAllTimePreset: dateRangeField.includeAllTimePreset !== false,
              label: showLabels ? dateRangeField.label : undefined,
            }
          : undefined
      }
      search={
        hideSearch
          ? undefined
          : {
              value: filters[searchKey] || '',
              onChange: (v) => onFilterChange(searchKey, v),
              placeholder: resolvedSearchPlaceholder,
              label: resolvedSearchLabel,
            }
      }
      onClear={onClearFilters}
      onApply={onApply && !hasApplyCustom ? onApply : derivedOnApply}
      applyLabel={applyLabel}
      applyDisabled={applyDisabled}
      extraActions={actionNodes}
    >
      {gridFields.map((field) => {
        const fieldShowLabel = showLabels && field.showLabel !== false
        const fieldKey = field.key

        if (field.type === 'custom' && field.render) {
          return (
            <FilterPanelField key={fieldKey} label={fieldShowLabel ? field.label : undefined}>
              {field.render()}
            </FilterPanelField>
          )
        }

        if (field.type === 'select' && field.options) {
          return (
            <FilterPanelField key={fieldKey} label={fieldShowLabel ? field.label : undefined}>
              <Select
                value={selectValueFromExternal(filters[field.key])}
                onValueChange={(v) => onFilterChange(field.key, selectValueToExternal(v))}
              >
                <SelectTrigger className="h-10 w-full rounded-xl text-sm shadow-sm">
                  <SelectValue placeholder={field.placeholder || field.label} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SELECT_ALL_VALUE}>{field.placeholder || field.label}</SelectItem>
                  {field.options.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FilterPanelField>
          )
        }

        if (field.type === 'date') {
          return (
            <FilterPanelField key={fieldKey} label={fieldShowLabel ? field.label : undefined}>
              <DatePicker
                value={filters[field.key] || null}
                onChange={(v) => onFilterChange(field.key, v || '')}
                placeholder={field.placeholder || field.label}
                mode={field.mode || 'single'}
                clearable={field.clearable !== false}
              />
            </FilterPanelField>
          )
        }

        if (field.type === 'text') {
          return (
            <FilterPanelField key={fieldKey} label={fieldShowLabel ? field.label : undefined}>
              <Input
                value={filters[field.key] || ''}
                onChange={(e) => onFilterChange(field.key, e.target.value)}
                placeholder={field.placeholder || field.label}
                className="h-10 min-h-10 rounded-xl border-border-subtle text-sm shadow-sm"
              />
            </FilterPanelField>
          )
        }

        return null
      })}
    </FilterPanel>
  )
}
